import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import { FaceCategory } from "building-types/materials/faceCategories";
import { materialSrcFromPalette } from "doc/materialLookup";
import { UVGeneration } from "geom/uv";
import { buildInteriorLayers } from "./buildingGeometryTools";

type TrimMolding = "baseTrimMoldings" | "crownTrimMoldings";

type BuildingForTrimGeometries<T extends TrimMolding> = Pick<Building, T | "interiorPalette">;

export function getBaseTrimMoldingGeometries(
  building: BuildingForTrimGeometries<"baseTrimMoldings">,
  emitter: BuildingGeometryEmitter,
): void {
  const { baseTrimMoldings, interiorPalette } = building;

  for (const trimId in baseTrimMoldings) {
    const { trims, roomSet, material: baseBoardMaterial } = baseTrimMoldings[trimId];

    const room = roomSet[0];

    const entityHandleForSelectingReportingUnit = roomEntityHandleForSelectingReportingUnit(room);

    for (const trim of trims) {
      emitter.emit({
        geom: trim,
        faceCategory: FaceCategory.BASE_TRIM_MOLDING,
        threeLayers: buildInteriorLayers(true),
        pickHandle: room.pickHandle,
        entityHandleForSelectingReportingUnit,
        materialSlot: baseBoardMaterial,
        materialSrc: materialSrcFromPalette(interiorPalette, baseBoardMaterial, room.type),
        uvGen: UVGeneration.GLOBAL_RECT_UV,
      });
    }
  }
}

export function getCrownTrimMoldingGeometries(
  building: BuildingForTrimGeometries<"crownTrimMoldings">,
  emitter: BuildingGeometryEmitter,
): void {
  const { crownTrimMoldings, interiorPalette } = building;

  for (const trimId in crownTrimMoldings) {
    const { trims, roomSet, material: crownMoldingMaterial } = crownTrimMoldings[trimId];

    const room = roomSet[0];

    const entityHandleForSelectingReportingUnit = roomEntityHandleForSelectingReportingUnit(room);

    for (const trim of trims) {
      emitter.emit({
        geom: trim,
        faceCategory: FaceCategory.CROWN_TRIM_MOLDING,
        threeLayers: buildInteriorLayers(true),
        pickHandle: room.pickHandle,
        entityHandleForSelectingReportingUnit,
        materialSlot: crownMoldingMaterial,
        materialSrc: materialSrcFromPalette(interiorPalette, crownMoldingMaterial, room.type),
        uvGen: UVGeneration.GLOBAL_RECT_UV,
      });
    }
  }
}

export function getInteriorWallPlaneTrimMoldingGeometries(
  building: Pick<Building, "interiorWallPlaneTrimMoldings" | "interiorPalette">,
  emitter: BuildingGeometryEmitter,
): void {
  const { interiorWallPlaneTrimMoldings, interiorPalette } = building;

  for (const trimId in interiorWallPlaneTrimMoldings) {
    const molding = interiorWallPlaneTrimMoldings[trimId];
    const room = molding.roomSet[0];
    const entityHandleForSelectingReportingUnit = {
      type: "WALL_PLANE_TRIM" as const,
      id: molding.sourceId,
    };

    for (const trim of molding.trims) {
      emitter.emit({
        geom: trim,
        faceCategory: FaceCategory.WALL_PLANE_TRIM_MOLDING,
        threeLayers: buildInteriorLayers(true),
        pickHandle: entityHandleForSelectingReportingUnit,
        entityHandleForSelectingReportingUnit,
        materialSlot: molding.material,
        materialSrc: materialSrcFromPalette(interiorPalette, molding.material, room?.type),
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        materialTransformation: molding.materialTransformation,
      });
    }
  }
}
